'use client'

import { useCallback, useMemo, useState } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import MarketRateValue from '@/components/market/MarketRateValue'
import {
  formatQuoteLabel,
  getInstrumentMeta,
  usesDollarQuoteDisplay,
  type MarketHistoryRange,
  type MarketInstrumentKey,
} from '@/lib/market-rates'
import { formatSiteDateTime } from '@/lib/site-datetime'
import { useMarketHistory } from '@/lib/use-market-history'

const RANGE_TABS: { key: MarketHistoryRange; label: string }[] = [
  { key: '1d', label: '1G' },
  { key: '7d', label: '7G' },
  { key: '30d', label: '1A' },
  { key: '1y', label: '1Y' },
]

const AXIS_TICK = { fontSize: 12, fill: '#64748b' }
const GRID_STROKE = '#e2e8f0'

type ChartPoint = {
  t: string
  rate: number
  label: string
}

function MarketChartDot(props: { cx?: number; cy?: number; color: string }) {
  const { cx, cy, color } = props
  if (cx == null || cy == null) return null
  return <circle cx={cx} cy={cy} r={3.5} fill={color} stroke="none" />
}

function MarketChartActiveDot(props: { cx?: number; cy?: number; color: string }) {
  const { cx, cy, color } = props
  if (cx == null || cy == null) return null
  return (
    <circle
      cx={cx}
      cy={cy}
      r={5}
      fill={color}
      stroke="#ffffff"
      strokeWidth={2}
    />
  )
}

function computeYDomain(rates: number[], decimals: number): [number, number] {
  if (!rates.length) return [0, 1]

  const min = Math.min(...rates)
  const max = Math.max(...rates)
  const minTick = Math.pow(10, -decimals)

  if (min === max) {
    const pad = Math.max(Math.abs(max) * 0.003, minTick * 5)
    return [min - pad, max + pad]
  }

  const spread = max - min
  const pad = Math.max(spread * 0.12, minTick * 2)
  return [min - pad, max + pad]
}

/** Y ekseni: daha az ondalık, okunaklı kısa format */
function formatChartAxisValue(value: number, decimals: number) {
  if (!Number.isFinite(value)) return ''
  const abs = Math.abs(value)
  const axisDecimals = abs >= 10_000 ? 0 : abs >= 1_000 ? 0 : abs >= 100 ? 1 : decimals
  return new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: axisDecimals,
    maximumFractionDigits: axisDecimals,
  }).format(value)
}

function formatTooltipDate(iso: string) {
  return formatSiteDateTime(iso, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

type MarketChartTooltipProps = {
  active?: boolean
  payload?: ReadonlyArray<{ payload?: ChartPoint }>
  instrumentKey: MarketInstrumentKey
}

function MarketChartTooltip({
  active,
  payload,
  instrumentKey,
}: MarketChartTooltipProps) {
  if (!active || !payload?.length) return null

  const row = payload[0]?.payload
  if (!row || typeof row.rate !== 'number' || !Number.isFinite(row.rate)) return null

  const meta = getInstrumentMeta(instrumentKey)
  const quoteLabel = formatQuoteLabel(meta)
  const showQuoteSuffix = !usesDollarQuoteDisplay(instrumentKey)

  return (
    <div className="pointer-events-none rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-lg dark:border-slate-600 dark:bg-slate-900">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
        {row.t ? formatTooltipDate(row.t) : row.label}
      </p>
      <p className="mt-1 flex flex-wrap items-center gap-1 text-sm font-bold text-gray-900 dark:text-gray-100">
        <MarketRateValue instrumentKey={instrumentKey} rate={row.rate} decimals={meta.decimals} size="md" />
        {showQuoteSuffix ? (
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">{quoteLabel}</span>
        ) : null}
      </p>
    </div>
  )
}

type MarketHistoryChartProps = {
  code: MarketInstrumentKey
  className?: string
}

export default function MarketHistoryChart({ code, className = '' }: MarketHistoryChartProps) {
  const [range, setRange] = useState<MarketHistoryRange>('7d')
  const { points, loading, error } = useMarketHistory(code, range)
  const meta = getInstrumentMeta(code)

  const chartData = useMemo<ChartPoint[]>(
    () =>
      points.map((p) => ({
        t: p.t,
        rate: p.rate,
        label: formatSiteDateTime(p.t, {
          day: '2-digit',
          month: 'short',
          hour: range === '1y' || range === '30d' ? undefined : '2-digit',
          minute: range === '1d' || range === '7d' ? '2-digit' : undefined,
        }),
      })),
    [points, range],
  )

  const yDomain = useMemo(
    () => computeYDomain(chartData.map((p) => p.rate), meta.decimals),
    [chartData, meta.decimals],
  )

  const chartColor = useMemo(() => {
    if (chartData.length < 2) return '#10b981'
    return chartData[chartData.length - 1].rate >= chartData[0].rate ? '#10b981' : '#ef4444'
  }, [chartData])

  const gradientId = `marketFill-${code}-${range}`

  const renderTooltip = useCallback(
    (props: { active?: boolean; payload?: ReadonlyArray<{ payload?: ChartPoint }> }) => (
      <MarketChartTooltip {...props} instrumentKey={code} />
    ),
    [code],
  )

  const renderDot = useCallback(
    (props: { cx?: number; cy?: number }) => <MarketChartDot {...props} color={chartColor} />,
    [chartColor],
  )

  const renderActiveDot = useCallback(
    (props: { cx?: number; cy?: number }) => (
      <MarketChartActiveDot {...props} color={chartColor} />
    ),
    [chartColor],
  )

  return (
    <div className={`rounded-xl border border-gray-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5 ${className}`}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">Geçmiş Grafik</h2>
        <div className="flex rounded-lg bg-gray-100 p-1 dark:bg-slate-900">
          {RANGE_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setRange(tab.key)}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                range === tab.key
                  ? 'bg-white text-gray-900 shadow-sm dark:bg-slate-800 dark:text-gray-100'
                  : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex h-72 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-red-500" />
        </div>
      ) : error ? (
        <div className="flex h-72 items-center justify-center text-sm text-gray-500 dark:text-gray-400">
          {error}
        </div>
      ) : chartData.length === 0 ? (
        <div className="flex h-72 flex-col items-center justify-center gap-2 text-center text-sm text-gray-500 dark:text-gray-400">
          <p>Henüz yeterli geçmiş veri yok.</p>
          <p className="text-xs">Veriler birkaç saat içinde birikmeye başlayacak.</p>
        </div>
      ) : (
        <div className="h-72 w-full min-h-[18rem]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 8, right: 4, left: 4, bottom: 4 }}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartColor} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={chartColor} stopOpacity={0.04} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={GRID_STROKE} strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                tick={AXIS_TICK}
                minTickGap={28}
                interval="preserveStartEnd"
                axisLine={false}
                tickLine={false}
                dy={6}
              />
              <YAxis
                orientation="right"
                domain={yDomain}
                tick={AXIS_TICK}
                width={72}
                tickCount={5}
                allowDecimals
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => formatChartAxisValue(Number(v), meta.decimals)}
              />
              <Tooltip
                cursor={{ stroke: '#94a3b8', strokeWidth: 1, strokeDasharray: '4 4' }}
                content={renderTooltip}
                wrapperStyle={{ outline: 'none', zIndex: 20 }}
              />
              <Area
                type="monotone"
                dataKey="rate"
                name={meta.label}
                stroke={chartColor}
                fill={`url(#${gradientId})`}
                strokeWidth={2}
                dot={chartData.length <= 8 ? renderDot : false}
                activeDot={renderActiveDot}
                baseValue={yDomain[0]}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}
